import { type FilterChipsModalTab, type SelectedFilterChipsModalItems, useIntl } from '@capsicum/common';
import { type PrimitiveAtom } from 'jotai';
import styled, { useTheme } from 'styled-components';

import { useFilterChipsModal } from '../../../hooks/filter-modal/useFilterChipsModal';
import { Button } from '../../atoms/buttons/button/Button';
import { Chip } from '../../atoms/chip/Chip';
import { ChevronLeftBoldIcon } from '../../icons/ChevronLeftBold';
import { CrossIcon } from '../../icons/Cross';
import { BaseSearchInput } from '../form/search-input/BaseSearchInput';
import { HeaderBlock } from '../header-block/HeaderBlock';
import { HeaderTabView } from '../header-tab-view/HeaderTabView';

import { FilterChipsModalContent } from './FilterChipsModalContent';

export type FilterChipsModalProps = {
	atom: PrimitiveAtom<SelectedFilterChipsModalItems>;
	closeModal: () => void;
	defaultTab: string;
	onApplyFilters?: (filters: SelectedFilterChipsModalItems) => void;
	tabs: Array<FilterChipsModalTab>;
	testID?: string;
};

export const FilterChipsModal = ({
	atom,
	closeModal,
	defaultTab,
	onApplyFilters,
	tabs,
	testID
}: FilterChipsModalProps) => {
	const { $t } = useIntl();
	const { colors } = useTheme();
	const {
		handleApplyFilters,
		handleClearSearchInput,
		handleItemSelect,
		handleOnClear,
		handleSearchInputChange,
		handleTabChange,
		height,
		isItemSelected,
		removeItemSelected,
		searchInputRef,
		searchText,
		selectedItems,
		selectedTab,
		setShouldClearText,
		shouldClearText,
		showTabs,
		tabContent
	} = useFilterChipsModal(tabs, defaultTab, closeModal, atom, onApplyFilters);

	const onBackButton = () => {
		closeModal?.();
	};

	return (
		<S.ModalFilterWrapper $height={height}>
			<S.BackHeaderBlock>
				<S.BackButton onClick={() => onBackButton()}>
					<ChevronLeftBoldIcon color={colors.icons.main.enabled} />
				</S.BackButton>
				<S.HeaderBlockWrapper>
					<HeaderBlock
						actionText={$t('global.clear')}
						testID={`${testID}-header`}
						title={$t('global.filter')}
						onActionClick={handleOnClear}
					/>
				</S.HeaderBlockWrapper>
			</S.BackHeaderBlock>

			{showTabs && (
				<HeaderTabView selectedTab={selectedTab} tabs={tabs} testID={testID} onTabChange={handleTabChange} />
			)}

			<BaseSearchInput
				debounceTime={250}
				placeholder={$t('global.search')}
				ref={searchInputRef}
				setShouldClearText={setShouldClearText}
				shouldClearText={shouldClearText}
				testID={`${testID}-searchInput-text`}
				value={searchText}
				onChange={handleSearchInputChange}
				onClear={handleClearSearchInput}
			/>
			<S.ChipsWrapper>
				<S.Selected>
					{selectedItems && Object.values(selectedItems).flat().length ? (
						Object.values(selectedItems)
							.flat()
							.map(item => (
								<S.StyledChip
									iconEnd={S.StyledCrossIcon}
									imageStart={item.icon}
									isSelected
									key={item.id}
									testID={`${testID}-selectedChip-${item.id}`}
									text={item.label}
									value={item.id}
									variant="brand"
									onClick={() => removeItemSelected(item)}
								/>
							))
					) : (
						<S.StyledChip
							iconEnd={S.StyledWhiteCrossIcon}
							testID={`${testID}-selectedChip-clearAll`}
							text={$t('global.all')}
							value="All"
							variant="neutral"
						/>
					)}
				</S.Selected>

				<S.ContentWrapper $height={height}>
					<FilterChipsModalContent
						isItemSelected={isItemSelected}
						items={tabContent}
						selectedTab={selectedTab}
						testID={testID}
						onItemSelect={handleItemSelect}
					/>
				</S.ContentWrapper>
			</S.ChipsWrapper>
			<S.ButtonWrapper>
				<S.StyledButton
					color="brand"
					disabled={false}
					testID={`${testID}-button-applyFilters`}
					text={$t('global.applyFilters')}
					onClick={handleApplyFilters}
				/>
			</S.ButtonWrapper>
		</S.ModalFilterWrapper>
	);
};

namespace S {
	export const ModalFilterWrapper = styled.div<{ $height: number }>`
		display: flex;
		flex-direction: column;
		gap: ${({ theme }) => theme.spacings.medium};
		height: 100%;
	`;

	export const HeaderBlockWrapper = styled.div`
		justify-content: center;
		flex: 9;
	`;

	export const BackHeaderBlock = styled.div`
		display: flex;
		justify-content: center;
	`;

	export const BackButton = styled.button`
		height: fit-content;
		text-decoration: none;
		border: none;
		background: transparent;
		flex: 1;
	`;

	export const ButtonWrapper = styled.div`
		display: flex;
		align-self: center;
		width: 100%;
	`;

	export const StyledButton = styled(Button)`
		${({ theme }) => theme.border.radius.circle}
		width: 95%;
	`;

	export const Selected = styled.div`
		display: flex;
		flex-direction: row;
		flex-wrap: wrap;
	`;

	export const ContentWrapper = styled.div<{ $height: number }>`
		display: flex;
		height: ${({ $height }) => $height * 0.9};
		overflow-y: scroll;
	`;

	export const StyledWhiteCrossIcon = styled(CrossIcon)`
		color: ${({ theme }) => theme.colors.text.onBrand.enabled};
	`;

	export const StyledCrossIcon = styled(CrossIcon)`
		color: ${({ theme }) => theme.colors.icons.brand.enabled};
	`;

	export const StyledChip = styled(Chip)`
		margin-bottom: ${({ theme }) => `${theme.spacings.xsmall}`};
	`;

	export const ChipsWrapper = styled.div`
		height: 80%;
		overflow: hidden;
		overflow-y: scroll;
		gap: ${({ theme }) => theme.spacings.medium};
		display: flex;
		flex-direction: column;
	`;
}
